import {
  type AccessUser,
  type Concessions,
  type PropertyComparableResponse,
  type PropertyDetailsResponse,
  type PropertyEdits,
  type PropertySearchResponse,
  type UnitAmenityTag,
  type UserAttributes,
  Section,
  Column,
  SectionColumn,
  CancelablePromise,
  CancelError,
  UnitAmenity,
  FeatureFlag
} from 'common'
import { parseISO, format, intervalToDuration, formatDuration } from 'date-fns'
import { colors } from 'quasar'
import auth0Plugin from '@/setups/authSetup'
import { useStripeStore } from '@/stores/stripe'
import type { RouteLocationRaw } from 'vue-router'

export type ColumnData = Record<
  Column,
  {
    description: string
    label: string
  }
>

export interface WizardStep {
  title: string
  name: string
  previousStep?: RouteLocationRaw
  nextStep?: RouteLocationRaw
}

// FORMAT FUNCTIONS

export const formatQuality = (val: number | null | undefined, missing = ''): string => {
  return val !== null && val !== undefined ? `${(val * 10).toFixed(0)}/10` : missing
}

// TODO: use the one from common/utils.ts instead
export const formatPercent = (
  val: number | null,
  decimal = 1,
  plusSign = false,
  missing = ''
): string => {
  if (plusSign && val !== null && val >= 0) {
    return `+${(val * 100).toFixed(decimal)}%`
  }
  return val !== null ? `${(val * 100).toFixed(decimal)}%` : missing
}

// TODO: use the one from common/utils.ts instead
export const formatMoney = (val: number | null, decimals?: number): string => {
  if (decimals !== undefined) {
    return val != null
      ? `$${val.toLocaleString('en-US', {
          minimumFractionDigits: decimals,
          maximumFractionDigits: decimals
        })}`
      : ''
  }
  return val != null ? `$${val.toLocaleString('en-US')}` : ''
}

// TODO: use the one from common/utils.ts instead
export const formatNumber = (
  val: number | null,
  sign: boolean = false,
  digits: number = 0,
  thousandsSeparator: boolean = true
): string => {
  const options = {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
    useGrouping: thousandsSeparator
  }
  if (sign === true && val != null) {
    return val > 0
      ? `+${val.toLocaleString('en-US', options)}`
      : val.toLocaleString('en-US', options)
  }
  return val !== null ? val.toLocaleString('en-US', options) : ''
}

export const formatDate = (val: string | null, displayTime: boolean = true): string => {
  return val ? format(parseISO(val), displayTime ? 'M/d/yyyy, h:mm a' : 'M/d/yyyy') : ''
}

export const daysSince = (val: string | null): string => {
  if (!val) {
    return ''
  }
  const date = parseISO(val)
  const now = new Date()
  const duration = intervalToDuration({ start: date, end: now })

  if (duration.years && duration.years > 0) {
    return 'Over ' + formatDuration(duration, { format: ['years'] }) + ' ago'
  }
  if (duration.months && duration.months > 0) {
    return 'Over ' + formatDuration(duration, { format: ['months'] }) + ' ago'
  }
  if (duration.days && duration.days >= 7) {
    const weeks = Math.floor(duration.days / 7)
    return 'Over ' + formatDuration({ weeks }, { format: ['weeks'] }) + ' ago'
  }
  if (duration.days && duration.days > 0) {
    return 'Over ' + formatDuration(duration, { format: ['days'] }) + ' ago'
  }
  return 'Today'
}

export const formatKey = (label: string): string => {
  return label
    .replace(/^(avg_quality_score_|property_|quality)/g, '')
    .split('_')
    .map((_) => _.slice(0, 1).toUpperCase() + _.slice(1))
    .join(' ')
}

export const formatFloorplanName = (name: string | null, beds: number | null): string => {
  // formats floorplan name to fill
  if (name) {
    return name
  } else if (beds === 0) {
    return 'Studio'
  }
  return ''
}

// AGGREGATION FUNCTIONS

export const avg = (vals: (number | null)[]): number | null => {
  const filtered = vals.filter((val): val is number => val !== null)
  return filtered.length > 0 ? filtered.reduce((a, b) => a + b, 0) / filtered.length : null
}

export const min = (values: number[]): number | null => {
  return values.length ? Math.min(...values) : null
}

export const max = (values: number[]): number | null => {
  return values.length ? Math.max(...values) : null
}

export const sum = (vals: (number | null)[]): number => {
  const filtered = vals.filter((val): val is number => val !== null)
  return filtered.length > 0 ? filtered.reduce((a, b) => a + b, 0) : 0
}

export const percentile = (perc: number, vals: number[]): number => {
  const sorted = vals.filter((_) => _ !== null).sort((a, b) => a - b)
  const index = Math.floor((sorted.length - 1) * perc)
  return sorted[index]
}

/**
 * Allows one to apply multiple rolling functions to an array of values, for example avg, min, max, sum, percentile, etc.
 * Note: this is a centered rolling window, and we narrow the window on the edges.
 *
 * @param arr the array of values to apply the rolling function to
 * @param windowSize the size of the rolling window. Should be an odd number, since it's centered.
 * @param fn the function to apply to the rolling window
 * @returns
 */
export function rollingFunctions<T>(
  arr: number[][],
  windowSize: number,
  fn: (vals: number[]) => T,
  trailing: boolean = false
): T[] {
  const half = Math.floor(windowSize / 2)
  const results = Array<T>(arr.length)

  for (let dateIdx = 0; dateIdx < arr.length; dateIdx++) {
    // compute the rolling window for each function
    const localArray: number[] = []

    if (!trailing) {
      for (let j = dateIdx - half; j <= dateIdx + half; j++) {
        if (j >= 0 && j < arr.length) {
          localArray.push(...arr[j])
        }
      }
    } else {
      for (let j = Math.max(0, dateIdx - windowSize + 1); j <= dateIdx; j++) {
        if (j >= 0 && j < arr.length) {
          localArray.push(...arr[j])
        }
      }
    }
    results[dateIdx] = fn(localArray)
  }

  return results
}

/**
 * Returns a function that calculates the percentiles of an array of values.
 * @param percs the percentiles to calculate
 * @returns a function that calculates the percentiles of an array of values
 */
export const percentileFn = (percs: number[]): ((arr: (number | null)[]) => (number | null)[]) => {
  if (percs.length === 0) {
    return () => []
  }
  return (arr: (number | null)[]) => {
    const sorted = arr.filter((val): val is number => val !== null).sort((a, b) => a - b)
    const lastIndex = sorted.length - 1
    if (sorted.length < arr.length) {
      return percs.map(() => null)
    }
    return percs.map((perc) => sorted[Math.floor(lastIndex * perc)])
  }
}

/**
 * Returns the mean and standard deviation of an array of values.
 * @param arr the array of values to calculate the mean and standard deviation of
 * @param usePopulation whether to use the population standard deviation or the sample standard deviation
 * @returns the mean and standard deviation of the array
 */
export const normalDistribution = (
  arr: number[],
  usePopulation: boolean = false
): { mean: number | null; stddev: number | null } => {
  if (arr.length === 0) {
    return { mean: null, stddev: null }
  }
  const mean = arr.reduce((acc, val) => acc + val, 0) / arr.length

  const stddev = Math.sqrt(
    arr
      .reduce((acc, val) => acc.concat((val - mean) ** 2), [] as number[])
      .reduce((acc, val) => acc + val, 0) /
      (arr.length - (usePopulation ? 0 : 1))
  )
  return { mean, stddev }
}

// GOOGLE MAP

export const lightMapTheme = [
  {
    elementType: 'geometry',
    stylers: [
      {
        color: '#f5f5f5'
      }
    ]
  },
  {
    elementType: 'labels.text.fill',
    stylers: [
      {
        color: '#555555'
      }
    ]
  },
  {
    elementType: 'labels.text.stroke',
    stylers: [
      {
        color: '#f5f5f5'
      }
    ]
  },
  {
    featureType: 'administrative.land_parcel',
    elementType: 'labels',
    stylers: [
      {
        visibility: 'off'
      }
    ]
  },
  {
    featureType: 'administrative.land_parcel',
    elementType: 'labels.text.fill',
    stylers: [
      {
        color: '#555555'
      }
    ]
  },
  {
    featureType: 'landscape.man_made',
    stylers: [
      {
        visibility: 'off'
      }
    ]
  },
  {
    featureType: 'poi',
    elementType: 'geometry',
    stylers: [
      {
        color: '#eeeeee'
      }
    ]
  },
  {
    featureType: 'poi',
    elementType: 'labels.text.fill',
    stylers: [
      {
        color: '#555555'
      }
    ]
  },
  {
    featureType: 'poi.attraction',
    stylers: [
      {
        visibility: 'off'
      }
    ]
  },
  {
    featureType: 'poi.business',
    stylers: [
      {
        visibility: 'off'
      }
    ]
  },
  {
    featureType: 'poi.government',
    stylers: [
      {
        visibility: 'off'
      }
    ]
  },
  {
    featureType: 'poi.medical',
    stylers: [
      {
        visibility: 'off'
      }
    ]
  },
  {
    featureType: 'poi.park',
    elementType: 'geometry',
    stylers: [
      {
        color: '#e5e5e5'
      }
    ]
  },
  {
    featureType: 'poi.park',
    elementType: 'labels.text',
    stylers: [
      {
        visibility: 'off'
      }
    ]
  },
  {
    featureType: 'poi.park',
    elementType: 'labels.text.fill',
    stylers: [
      {
        color: '#555555'
      }
    ]
  },
  {
    featureType: 'poi.place_of_worship',
    stylers: [
      {
        visibility: 'off'
      }
    ]
  },
  {
    featureType: 'poi.school',
    stylers: [
      {
        visibility: 'off'
      }
    ]
  },
  {
    featureType: 'poi.sports_complex',
    stylers: [
      {
        visibility: 'off'
      }
    ]
  },
  {
    featureType: 'road',
    elementType: 'geometry',
    stylers: [
      {
        color: '#ffffff'
      }
    ]
  },
  {
    featureType: 'road.arterial',
    elementType: 'labels.text.fill',
    stylers: [
      {
        color: '#888888'
      }
    ]
  },
  {
    featureType: 'road.highway',
    elementType: 'geometry',
    stylers: [
      {
        color: '#dadada'
      }
    ]
  },
  {
    featureType: 'road.highway',
    elementType: 'labels.text.fill',
    stylers: [
      {
        color: '#555555'
      }
    ]
  },
  {
    featureType: 'road.local',
    elementType: 'labels',
    stylers: [
      {
        visibility: 'off'
      }
    ]
  },
  {
    featureType: 'road.local',
    elementType: 'labels.text.fill',
    stylers: [
      {
        color: '#999999'
      }
    ]
  },
  {
    featureType: 'transit.line',
    elementType: 'geometry',
    stylers: [
      {
        color: '#e5e5e5'
      }
    ]
  },
  {
    featureType: 'transit.station',
    elementType: 'geometry',
    stylers: [
      {
        color: '#eeeeee'
      }
    ]
  },
  {
    featureType: 'water',
    elementType: 'geometry',
    stylers: [
      {
        color: '#c9c9c9'
      }
    ]
  },
  {
    featureType: 'water',
    elementType: 'labels.text.fill',
    stylers: [
      {
        color: '#555555'
      }
    ]
  },
  {
    featureType: 'poi',
    stylers: [
      {
        visibility: 'off'
      }
    ]
  }
]

export const darkMapTheme = [
  {
    elementType: 'geometry',
    stylers: [
      {
        color: '#222222' // Darker background color
      }
    ]
  },
  {
    elementType: 'labels.text.fill',
    stylers: [
      {
        color: '#dddddd' // Lighter text color
      }
    ]
  },
  {
    elementType: 'labels.text.stroke',
    stylers: [
      {
        color: '#222222' // Darker text stroke
      }
    ]
  },
  {
    featureType: 'administrative.land_parcel',
    elementType: 'labels',
    stylers: [
      {
        visibility: 'off'
      }
    ]
  },
  {
    featureType: 'landscape.man_made',
    stylers: [
      {
        visibility: 'off'
      }
    ]
  },
  {
    featureType: 'poi',
    elementType: 'geometry',
    stylers: [
      {
        color: '#555555'
      }
    ]
  },
  {
    featureType: 'poi',
    elementType: 'labels.text.fill',
    stylers: [
      {
        color: '#555555'
      }
    ]
  },
  {
    featureType: 'road',
    elementType: 'geometry',
    stylers: [
      {
        color: '#555555' // Darker road geometry color
      }
    ]
  },
  {
    featureType: 'road.arterial',
    elementType: 'labels.text.fill',
    stylers: [
      {
        color: '#dddddd' // Lighter arterial road text color
      }
    ]
  },
  {
    featureType: 'road.highway',
    elementType: 'geometry',
    stylers: [
      {
        color: '#555555' // Darker highway geometry color
      }
    ]
  },
  {
    featureType: 'road.highway',
    elementType: 'labels.text.fill',
    stylers: [
      {
        color: '#dddddd' // Lighter highway text color
      }
    ]
  },
  {
    featureType: 'road.local',
    elementType: 'labels.text.fill',
    stylers: [
      {
        color: '#dddddd' // Lighter local road text color
      }
    ]
  },
  {
    featureType: 'transit.line',
    elementType: 'geometry',
    stylers: [
      {
        color: '#555555' // Darker transit line geometry color
      }
    ]
  },
  {
    featureType: 'transit.station',
    elementType: 'geometry',
    stylers: [
      {
        color: '#555555' // Darker transit station geometry color
      }
    ]
  },
  {
    featureType: 'water',
    elementType: 'geometry',
    stylers: [
      {
        color: '#555555' // Darker water geometry color
      }
    ]
  },
  {
    featureType: 'water',
    elementType: 'labels.text.fill',
    stylers: [
      {
        color: '#dddddd' // Lighter water text color
      }
    ]
  },
  {
    featureType: 'poi',
    stylers: [
      {
        visibility: 'off'
      }
    ]
  }
]

const baseColors = [
  '#2196f3',
  '#4caf50',
  '#ffc107',
  '#f44336',
  '#673ab7',
  '#00bcd4',
  '#009688',
  '#9c27b0'
]

const colorCache: Record<number, Record<number, string>> = Object.fromEntries(
  baseColors.map((_, i) => [i, {}])
)

/**
 * Returns different colors depending on the number of bedrooms
 * @param bed The number of bedrooms
 * @returns
 */
export const unitTypeColor = (bed: number | undefined | null, alpha: number = 1): string => {
  if (bed === undefined || bed === null || bed < 0 || bed > 7) {
    return colors.getPaletteColor('grey')
  }

  // Round alpha to nearest 0.1 to use cached values
  const roundedAlpha = Math.round(alpha * 10) / 10

  if (colorCache[bed][roundedAlpha]) {
    return colorCache[bed][roundedAlpha]
  }
  const color = alpha === 1 ? baseColors[bed] : colors.changeAlpha(baseColors[bed], alpha)
  colorCache[bed][roundedAlpha] = color

  return color
}

export const diagonalChartBg = (color: string, ctx: CanvasRenderingContext2D): CanvasPattern => {
  const shape = document.createElement('canvas')
  shape.width = 10
  shape.height = 10
  const c = shape.getContext('2d')!
  c.fillStyle = color
  c.fillRect(0, 0, 10, 10)
  c.strokeStyle = 'rgba(0, 0, 0, 0.25)'
  c.lineWidth = 1.5
  c.beginPath()
  c.moveTo(2, 0)
  c.lineTo(10, 8)
  c.stroke()
  c.beginPath()
  c.moveTo(0, 8)
  c.lineTo(2, 10)
  c.stroke()
  return ctx.createPattern(shape, 'repeat')!
}

export type ConstructionStatus =
  | 'In Planning'
  | 'Under Construction'
  | 'Scheduled Construction'
  | 'Construction Completed'

export const CONSTRUCTION_STATUS_ORDER = [
  'Construction Completed',
  'In Planning',
  'Scheduled Construction',
  'Under Construction'
] as const

export const getColorByConstructionStatus = (status: ConstructionStatus) => {
  switch (status) {
    case 'Construction Completed':
      return unitTypeColor(1, 0.6) // Green
    case 'In Planning':
      return unitTypeColor(0, 0.6) // Blue
    case 'Scheduled Construction':
      return unitTypeColor(3, 0.6) // Red
    case 'Under Construction':
      return unitTypeColor(2, 0.6) // Dirty yellow
    default:
      return '#9e9e9e' // Gray for unknown
  }
}

const qualityColors = [
  '#ecf4ea', // 1
  '#d8e8d6', // 2
  '#c5ddc2', // 3
  '#b2d2ae', // 4
  '#9fc79b', // 5
  '#8bbc87', // 6
  '#78b074', // 7
  '#64a562', // 8
  '#4f9a4f', // 9
  '#327934' // 10
]

export const similarityColor = (percentage: number | null): string => {
  if (percentage === null) {
    return qualityColors[0]
  }

  // Map percentage to color index (0-9)
  const colorIndex = Math.min(Math.floor(percentage * 10) - 1, qualityColors.length - 1)
  if (colorIndex < 0) {
    return qualityColors[0]
  }
  return qualityColors[colorIndex]
}

export const similarityTextColor = (percentage: number | null): string => {
  if (percentage === null) {
    return 'white'
  }

  // Map percentage to color index (0-9)
  const colorIndex = Math.min(Math.floor(percentage * 10), qualityColors.length - 1)
  if (colorIndex <= 3) {
    return qualityColors[9]
  }
  return 'white'
}

export const propertyDetailToSearch = (
  property: PropertyDetailsResponse
): PropertySearchResponse => {
  return {
    id: property.id,
    lat: property.lat,
    lon: property.lon,
    building_name: property.building_name,
    building_name_alias: property.building_name_alias,
    street_address: property.street_address,
    street_address_alias: property.street_address_alias,
    city: property.city,
    state: property.state,
    zip_code: property.zip_code,
    number_units: property.number_units,
    year_built: property.year_built,
    user_id: property.user_id
  }
}

export const compResponseToSearch = (comp: PropertyComparableResponse): PropertySearchResponse => {
  return {
    id: comp.id,
    lat: comp.lat,
    lon: comp.lon,
    building_name: comp.building_name,
    building_name_alias: comp.building_name_alias,
    street_address: comp.street_address,
    street_address_alias: comp.street_address_alias,
    city: comp.city,
    state: comp.state,
    zip_code: comp.zip_code,
    number_units: comp.number_units,
    year_built: comp.year_built
  }
}

/**
 * Generate a list of property edits based on the manual changes made to the property details.
 */
export const computePropertyEdits = (
  changes: Record<string, Record<string, string | number>>
): PropertyEdits[] => {
  if (!changes) {
    return []
  }
  return Object.entries(changes).map(([propertyId, details]) => {
    return {
      report_id: -1,
      property_id: propertyId,
      user_id: '',
      ...details
    }
  })
}

export const deconstructPropertyEdits = (
  edits: PropertyEdits[]
): Record<string, Record<string, string | number>> => {
  const result: Record<string, Record<string, string | number>> = {}
  for (const edit of edits) {
    if (!result[edit.property_id]) {
      result[edit.property_id] = {}
    }
    for (const key in edit) {
      if (
        key !== 'property_id' &&
        key !== 'report_id' &&
        key !== 'user_id' &&
        edit[key as keyof PropertyEdits] !== null
      ) {
        result[edit.property_id][key] = edit[key as keyof PropertyEdits] as string | number
      }
    }
  }
  return result
}

export const userToName = (
  user?: AccessUser | UserAttributes | null,
  preferMeLabel: boolean = true
): string => {
  if (user) {
    if (user.id === auth0Plugin.user.value?.sub && preferMeLabel) {
      return 'me'
    }
    if (user.given_name && user.family_name) {
      return `${user.given_name} ${user.family_name}`
    }
    return user.email
  }
  return 'Unknown User'
}

export type TabName =
  | 'overview'
  | 'property'
  | 'unit'
  | 'feesAndAmenities'
  | 'expenseBenchmarks'
  | 'revenueManagement'
  | 'taxHistory'
  | 'debug'

export interface TabComponentColumn {
  label: string
  description: string
  name: Column
}

export interface TabComponent {
  label: string
  name: Section
  description: string
  authorized: boolean
  columns?: TabComponentColumn[]
}

export interface Tab {
  label: string
  name: TabName
  authorized: boolean
  components: TabComponent[]
}

export interface VisibleColumn extends TabComponentColumn {
  isVisible: boolean
}

export interface VisibleComponent extends TabComponent {
  isVisible: boolean
  columns: VisibleColumn[]
  showColumns: boolean
}

export interface TabWithVisibleComponents extends Tab {
  components: VisibleComponent[]
}

export const getApplicationTabs = (): Tab[] => {
  return [
    {
      label: 'Overview',
      name: 'overview',
      authorized: true,
      components: [
        {
          label: 'Rent Comps Grid',
          name: Section.PROPERTY_GRID,
          authorized: true,
          description:
            'Side by side comparisons of comps with a plethora of columns to choose from.'
        },
        {
          label: 'Market Alerts Table',
          name: Section.MARKET_ALERTS,
          authorized: true,
          description:
            'Identify recent trend changes between the subject property and selected comparables.'
        },
        {
          label: 'Points of Interest Map',
          name: Section.POI_MAP,
          authorized: true,
          description: 'See nearby points of interest on the map.'
        }
      ]
    },
    {
      label: 'Rent Comps',
      name: 'property',
      authorized: true,
      components: [
        {
          label: 'Map',
          name: Section.COMP_MAP,
          authorized: true,
          description: 'See your comps displayed on a map.'
        },
        {
          label: 'Rent Comps Table',
          name: Section.BUILDING_INFORMATION,
          authorized: true,
          description:
            'Compare your performance to the most statistically similar properties in your market.',
          columns: [
            {
              label: 'Similarity',
              name: Column.SIMILARITY,
              description:
                "A percentage from 0 to 100, where 100 means it's the most similar to your property. The score is based on several factors, which you can see by clicking the score. We adjust the importance of each factor depending on the market."
            },
            {
              label: 'Leased % (Adv)',
              name: Column.ESTIMATED_OCCUPANCY,
              description:
                'The advertised leased percentage is the ratio of vacant units to total units. For stabilized assets, vacant units are defined as those available for move-in within the next 7 days.'
            },
            {
              label: 'Exposure',
              name: Column.EXPOSURE_PERCENT,
              description:
                'Exposure represents the percentage of units in a building currently listed as available for move-in within the next 6 months. This includes units that are either already vacant or not being renewed, divided by the total number of units in the building.'
            },
            {
              label: 'Year Built',
              name: Column.YEAR_BUILT,
              description:
                'The year the building was built. If no data is available, we show a prediction based on facade images.'
            },
            {
              label: '# Units',
              name: Column.NUMBER_UNITS,
              description:
                'The total number of units in the building. If no data is available, we show a prediction based on property characteristics, location and units that have been listed.'
            },
            {
              label: 'Stories',
              name: Column.NUMBER_STORIES,
              description:
                'The number of stories in the building. We show a prediction based on facade images if no data is available.'
            }
          ]
        },
        {
          label: 'Unit Type Cards',
          name: Section.UNIT_TYPE_METRICS,
          authorized: true,
          description: 'For each unit type in your comps, see name data points.'
        },
        {
          label: 'Rents by Unit Table',
          name: Section.UNIT_TYPE_TABLE,
          authorized: true,
          description: 'View rent and availability trends by unit type.',
          columns: [
            {
              label: '# Leased',
              name: Column.NUMBER_LEASED,
              description:
                'The total number of listings that have been leased within the selected time period.'
            },
            {
              label: '# Active',
              name: Column.CURRENTLY_AVAILABLE,
              description:
                'The total number of listings advertised as available for that property within the date range.'
            },
            {
              label: 'Days on Mkt',
              name: Column.DAYS_ON_MARKET,
              description:
                'Average number of days the units have been on the market. If a unit is still on the market, we include the number of days since it was first listed.'
            },
            {
              label: 'Min SF',
              name: Column.MIN_SQFT,
              description: 'Average minimum square footage of the units.'
            },
            {
              label: 'Avg SF',
              name: Column.SQFT,
              description: 'Average square footage of the units.'
            },
            {
              label: 'Max SF',
              name: Column.MAX_SQFT,
              description: 'Average maximum square footage of the units.'
            },
            {
              label: 'Min Rent',
              name: Column.MIN_MARKET_RENT,
              description:
                'Average minimum asking rent uses the most recent known asking rent for the units.'
            },
            {
              label: 'Avg Rent',
              name: Column.MARKET_RENT,
              description:
                'Average asking rent uses the most recent known asking rent for the units.'
            },
            {
              label: 'Max Rent',
              name: Column.MAX_MARKET_RENT,
              description:
                'Average maximum asking rent uses the most recent known asking rent for the units.'
            },
            {
              label: 'Avg PSF',
              name: Column.RENT_PSF,
              description:
                'Average asking rent per square foot uses the most recent known asking rent divided by the square footage of the unit.'
            },
            {
              label: 'NER',
              name: Column.EFFECTIVE_RENT,
              description:
                'Average effective rent uses the most recent known effective rents for the units. Our AI extracts the dollar amounts from concession banners and amortizes them over the lease term.'
            },
            {
              label: 'NER PSF',
              name: Column.EFFECTIVE_RENT_PSF,
              description:
                'Average effective rent per square foot uses the most recent known effective rent divided by the square footage of the unit.'
            },
            {
              label: 'Concession Amount',
              name: Column.CONCESSION_AMOUNT,
              description: 'Average monthly concession amount.'
            },
            {
              label: 'Total Concession Amount',
              name: Column.TOTAL_CONCESSION_AMOUNT,
              description:
                'Total concession over the lease term, calculated by multiplying the monthly concession amount by the number of months in the lease term.'
            },
            {
              label: 'Concession %',
              name: Column.CONCESSION_PERCENT,
              description: 'Average monthly concession amount as a percent of asking rent.'
            },
            {
              label: 'Year Built',
              name: Column.YEAR_BUILT,
              description:
                'The year the building was built. If no data is available, we show a prediction based on facade images.'
            },
            {
              label: 'Trend',
              name: Column.TREND,
              description: 'Effective Rent PSF or Asking Rent PSF trend over time.'
            },
            {
              label: 'TMLP',
              name: Column.TMLP,
              description:
                'Asking rent including all required fees, representing the total cost to the resident.'
            },
            {
              label: 'Net TMLP',
              name: Column.NET_TMLP,
              description:
                'Effective rent including all required fees, representing the total cost to the resident after concessions.'
            },
            {
              label: 'Req. Fees',
              name: Column.TMLP_FEES,
              description:
                'Total of monthly required fees. If blank, no monthly fees were found on property website.'
            }
          ]
        },
        {
          label: 'Estimated Rent Roll Table',
          name: Section.ESTIMATED_UNIT_MIX,
          authorized: true,
          description:
            'Analyze rents, availabilities and time on market by floorplan or type for your property and its comps.',
          columns: [
            {
              label: '# Listings',
              name: Column.NUMBER_LISTINGS,
              description: 'Number of unique units encountered of this type since June 17, 2023.'
            },
            {
              label: '# Units',
              name: Column.NUMBER_UNITS,
              description:
                "Estimated total units are calculated by multiplying the percentage of listings by the building's total number of units."
            },
            {
              label: 'Leased %',
              name: Column.ESTIMATED_OCCUPANCY,
              description:
                'Advertised leased percentage is the ratio of vacant units to total units. For stabilized assets, vacant units are defined as those available for move-in within the next 7 days.'
            },
            {
              label: 'Exposure %',
              name: Column.EXPOSURE_PERCENT,
              description:
                'Exposure represents the percentage of units in a building currently listed as available for move-in within the next 6 months. This includes units that are either already vacant or not being renewed, divided by the total number of units in the building.'
            },
            {
              label: 'Renewal %',
              name: Column.RENEWAL_PERCENT,
              description:
                'The estimated % of units leased in the trailing 12 months that did not re-enter the market, indicating they renewed.'
            },
            {
              label: 'Days on Mkt',
              name: Column.DAYS_ON_MARKET,
              description:
                'Number of days this unit has been on the market. If the unit is still on the market, we show the number of days since it was first listed.'
            },
            {
              label: '# Apps / Cancels',
              name: Column.NUMBER_APPS_TO_CANCELS,
              description:
                'Number of applications and cancellations or denials this unit experienced while on the market. If a unit was off the market for two or more days before returning, we consider it a cancellation.'
            },
            {
              label: 'Cancel %',
              name: Column.CANCEL_PERCENT,
              description:
                'Percent of applications that resulted in a cancellation. If a unit was off the market for two or more days before returning, we consider it a cancellation.'
            },
            {
              label: 'Leased',
              name: Column.LEASED_DATE,
              description:
                'Percent of applications that resulted in a cancellation. If a unit was off the market for two or more days before returning, we consider it a cancellation.'
            },
            {
              label: 'SF',
              name: Column.SQFT,
              description: 'The square footage of the unit.'
            },
            {
              label: 'Rent',
              name: Column.MARKET_RENT,
              description:
                'Last asking rent is the most recent known asking rent for this unit. When only a floorplan was found, a range of asking rents is shown instead.'
            },
            {
              label: 'PSF',
              name: Column.RENT_PSF,
              description:
                'Rent per sqft is the most recent known asking rent divided by the square footage of the unit.'
            },
            {
              label: 'Effective PSF',
              name: Column.EFFECTIVE_RENT_PSF,
              description:
                'Effective rent per sqft is the most recent known effective rent divided by the square footage of the unit.'
            },
            {
              label: 'NER',
              name: Column.EFFECTIVE_RENT,
              description:
                'Net effective rent is the most recent known effective rent for this unit. Our AI extracts the dollar amounts from concession banners and amortizes them over the lease term. If only a floorplan is available, a range of effective rents is displayed instead.'
            },
            {
              label: 'Tradeout',
              name: Column.TRADEOUT_PERCENT,
              description:
                'Lease tradeout shows the change in asking rents between the latest lease and the previous lease. Calculated as a trailing average over the last 18 months of leasing activity.'
            },
            {
              label: 'Trend',
              name: Column.TREND,
              description: 'Asking rent trend over time.'
            }
          ]
        },
        {
          label: 'Weekly Market Activity Chart',
          name: Section.VOLUME_OVER_TIME,
          authorized: true,
          description: 'Monitor market activity with detailed data on new and leased units.'
        },
        {
          label: 'Rent Comparison Chart',
          name: Section.CHART_COMPARE,
          authorized: true,
          description:
            'Quickly compare asking and effective rents by various metrics within your market.'
        },
        {
          label: 'Property Rankings Chart',
          name: Section.PROPERTY_RANKINGS_CHART,
          authorized: true,
          description: 'Quickly compare asking and effective rents by bed within your market.'
        },
        {
          label: 'Historical Rent Trends Chart',
          name: Section.RENT_TREND,
          authorized: true,
          description: 'Track historical min, max and average rent trends in your market.'
        },
        {
          label: 'Historical Exposure Chart',
          name: Section.EXPOSURE_CHART,
          authorized: true,
          description:
            'Analyze the percentage of units that are listed for rent for any given day or following 6 months.'
        },
        {
          label: 'Future Availability Chart',
          name: Section.FUTURE_AVAILABILITY,
          authorized: true,
          description:
            'Monitor the future supply of availabilities for units that have issued a notice to vacate.'
        },
        {
          label: '5-Year Rent Forecast Chart',
          name: Section.FIVE_YEAR_RENT_FORECAST,
          authorized: true,
          description: 'Analyze the projected rent growth for the subject property.'
        },
        {
          label: '5-Year Occupancy Forecast',
          name: Section.FIVE_YEAR_OCCUPANCY_FORECAST,
          authorized: true,
          description: 'Analyze the projected occupancy rates for the subject property.'
        },
        {
          label: 'Leasing Velocity Forecast',
          name: Section.LEASING_VELOCITY,
          authorized: true,
          description: 'Analyze the projected leasing velocity.'
        }
      ]
    },
    {
      label: 'Unit-Level Data',
      name: 'unit',
      authorized: true,
      components: [
        {
          label: 'Unit name Metrics',
          name: Section.UNIT_METRICS,
          authorized: true,
          description: "Compare name metrics about your subject's units against its comps."
        },
        {
          label: 'Unit-Level Data Table',
          name: Section.UNIT_DETAILS,
          authorized: true,
          description: 'Analyze competitor pricing and concessions with unit-level detail.'
        },
        {
          label: 'Lease Terms',
          name: Section.LEASE_TERMS,
          authorized: true,
          description: 'Monitor the best lease term offered across all units.'
        },
        {
          label: 'Price Recommendations Table',
          name: Section.PRICE_RECOMMENDATION,
          description:
            'Maximize revenue with unit-specific rent recommendations based on recently signed leases.',
          authorized: true
        },

        {
          label: 'Unit Prices Over Time Table',
          name: Section.UNIT_PRICES_OVER_TIME,
          authorized: true,
          description:
            'Analyze price trends and their correlation with new listings and lease closings.'
        }
      ]
    },
    {
      label: 'Fees & Amenities',
      name: 'feesAndAmenities',
      authorized: true,
      components: [
        {
          label: 'Fees Table',
          name: Section.FEES,
          authorized: true,
          description:
            'Compare fees charged by your subject property to what the market is charging.'
        },
        {
          label: 'Concessions Table',
          name: Section.SPECIALS_CONCESSIONS,
          authorized: true,
          description:
            'Compare concessions offered by your subject property to what the market is offering.'
        },
        {
          label: 'Finishes Table',
          name: Section.FINISHES,
          authorized: true,
          description:
            'Compare finish levels across your competition to improve market positioning.'
        },
        {
          label: 'Amenities Tables',
          name: Section.AMENITIES,
          authorized: true,
          description:
            'Compare building and unit amenities across your competition to improve market positioning.'
        }
      ]
    },
    {
      label: 'Financial Analysis',
      name: 'expenseBenchmarks',
      authorized: useStripeStore().hasFinancialAnalysis,
      components: [
        {
          label: 'Financial Analysis Table',
          name: Section.FINANCIAL_ANALYSIS,
          authorized: true,
          description:
            "Our estimate of the subject property's stabilized income and operating expenses over the next year, based on operating metrics from similar properties."
        },
        {
          label: 'Tax History',
          name: Section.TAX_HISTORY,
          authorized: true,
          description: 'Historical tax information for the subject property.'
        }
      ]
    },
    {
      label: 'Revenue Management',
      name: 'revenueManagement',
      authorized: useStripeStore().hasRevenueManagement,
      components: [
        {
          label: 'Rent Recommendation Highlights',
          name: Section.RECOMMENDATION_HIGHLIGHTS,
          authorized: true,
          description: "See key data points about your subject's renting habits"
        },
        {
          label: 'Rent Recommendation',
          name: Section.UNIT_MIX_RECOMMENDATION,
          authorized: true,
          description:
            'Leverage market dynamics among your comps to recommend the best rents for your units.'
        },
        {
          label: 'Volume Over Time',
          name: Section.FORECASTING,
          authorized: true,
          description:
            'Various charts documenting Leasing Velocity, Average Price, Exposure, Time on Market, and Vacancy.'
        }
      ]
    }
  ]
}

export const getTotalNumberOfColumns = () => {
  return getApplicationTabs().reduce((total, tab) => {
    return (
      total +
      tab.components.reduce((componentTotal, component) => {
        return componentTotal + (component.columns?.length || 0)
      }, 0)
    )
  }, 0)
}

export const getVisibleColumnsForSection = (
  visibleColumns: SectionColumn[],
  section: Section
): Column[] => {
  return visibleColumns.reduce((acc: string[], sectionColumn: string): Column[] => {
    const splitSectionColumn = sectionColumn.split('.') as Section[]
    if (splitSectionColumn[0] === section) {
      acc.push(splitSectionColumn[1])
    }
    return acc as Column[]
  }, [])
}

export const getToggleableColumnsForSection = (section: Section): ColumnData => {
  const sectionData = getApplicationTabs()
    .flatMap((tab) => tab.components)
    .find((_) => _.name === section)?.columns

  if (!sectionData) {
    throw new Error(`Section ${section} not found`)
  }

  return sectionData.reduce((acc: ColumnData, column) => {
    acc[column.name] = {
      description: column.description,
      label: column.label
    }
    return acc
  }, {} as ColumnData)
}

export const filterColumns = (
  columnName: Column,
  toggleableColumn: ColumnData,
  visibleColumns: Column[]
) => {
  if (columnName in toggleableColumn) {
    return visibleColumns.includes(columnName)
  }
  return true
}

export const sortColumns = (columnAName: Column, columnBName: Column, columnOrder: Column[]) => {
  const aIndex = columnOrder.indexOf(columnAName)
  const bIndex = columnOrder.indexOf(columnBName)

  if (aIndex === -1 || bIndex === -1) {
    console.error(`Column not found: ${columnAName} or ${columnBName}`)
    return 0
  }
  return aIndex - bIndex
}

export const hasConcessions = (
  property: PropertyDetailsResponse,
  dateRange: {
    from: Date
    to: Date
  }
) => {
  const fromStr = dateRange?.from ? format(dateRange.from, 'yyyy-MM-dd') : null
  const toStr = dateRange?.to ? format(dateRange.to, 'yyyy-MM-dd') : null
  return (property.concessions_history || []).some((concession) => {
    if (fromStr && toStr) {
      return !(concession.to_date < fromStr || concession.from_date > toStr)
    }
    return true
  })
}

// A Cancelable promise wrapper so that we can chain promises and
// keep the cancelable caracteristic

export class CancelablePromiseWrapper<T> {
  private readonly currentPromise: CancelablePromise<T>

  constructor(promise: CancelablePromise<T>) {
    this.currentPromise = promise
  }

  then<TResult1 = T, TResult2 = never>(
    onFulfilled?: ((value: T) => TResult1 | PromiseLike<TResult1>) | null,
    onRejected?: ((reason: any) => TResult2 | PromiseLike<TResult2>) | null
  ): CancelablePromiseWrapper<TResult1 | TResult2> {
    const newPromise = new CancelablePromise<TResult1 | TResult2>((resolve, reject, onCancel) => {
      this.currentPromise
        .then(
          (value) => {
            if (onFulfilled) {
              resolve(onFulfilled(value))
            } else {
              resolve(value as any)
            }
          },
          (reason) => {
            if (onRejected) {
              resolve(onRejected(reason))
            } else {
              reject(reason)
            }
          }
        )
        .catch(reject)

      onCancel(() => {
        try {
          this.currentPromise.cancel()
        } catch (error) {
          // We're expecting this error to be thrown if the user's cancelled it
          if (error instanceof CancelError) {
            return
          }

          // Otherwise it's an unexpected error that we want to throw
          throw error
        }
      })
    })

    return new CancelablePromiseWrapper(newPromise)
  }

  catch<TResult = never>(
    onRejected?: ((reason: any) => TResult | PromiseLike<TResult>) | null
  ): CancelablePromiseWrapper<T | TResult> {
    return this.then(null, onRejected)
  }

  cancel(): void {
    this.currentPromise.cancel()
  }

  // If you need to get the underlying promise
  unwrap(): CancelablePromise<T> {
    return this.currentPromise
  }
}

export const getTrendDiffPerc = (
  trend: { value: number | null; date: string }[],
  relative: boolean = false
): number | null => {
  if (!trend || trend.length < 2) {
    return null
  }

  const nonNullValues = trend.filter((point) => point.value !== null) as {
    value: number
    date: string
  }[]
  if (nonNullValues.length < 2) {
    return null
  }

  const first = nonNullValues[0]
  const last = nonNullValues[nonNullValues.length - 1]
  if (first.value === 0) {
    return null
  }
  const diff = last.value - first.value
  if (relative) {
    return diff / first.value
  }
  return diff
}

export const getActiveConcessions = (property: PropertyDetailsResponse): Concessions[] => {
  const activeConcessions = (property.concessions_history || []).filter((c) => {
    if (!c.to_date) {
      return false
    }
    return c.to_date >= property.created_on
  })

  if (activeConcessions.length > 1) {
    console.error('Error: More than one active concession found on property: ', property.id)
  }

  return activeConcessions
}

export const filterTags = (tags: UnitAmenityTag[]): UnitAmenityTag[] => {
  // TODO: Better handling of Direction Views, ideally include direction in amenity tag
  // per Marc, exclude this tag from the filter
  const excludedTags = ['view.direction']

  return tags.filter((tag) => !excludedTags.includes(tag))
}

export const getFloorplanAmenitiesAndTags = (
  data: { floorplan_name: string; units: { amenities: UnitAmenity[]; tags: UnitAmenityTag[] }[] }[]
) => {
  const amenityLookup: {
    [key: string]: { [amenity: string]: { count: number; frequency: number } }
  } = {}
  const tagLookup: { [key: string]: { [tag: string]: { count: number; frequency: number } } } = {}
  const totalCount: { [key: string]: number } = {}

  for (const row of data) {
    for (const unit of row.units) {
      const amenities = unit.amenities.filter(
        (_) =>
          !_.toLowerCase().includes(' floor') &&
          !_.toLowerCase().includes(' level') &&
          !_.toLowerCase().startsWith('floor ') &&
          !_.toLowerCase().startsWith('level ')
      )
      const tags = unit.tags || []

      if (amenities?.length || tags?.length) {
        if (!amenityLookup[row.floorplan_name]) {
          amenityLookup[row.floorplan_name] = {}
          tagLookup[row.floorplan_name] = {}
          totalCount[row.floorplan_name] = 0
        }
        totalCount[row.floorplan_name] += 1

        for (const amenity of amenities) {
          if (!amenityLookup[row.floorplan_name][amenity]) {
            amenityLookup[row.floorplan_name][amenity] = { count: 0, frequency: 0 }
          }
          amenityLookup[row.floorplan_name][amenity].count += 1
        }

        for (const tag of filterTags(tags)) {
          if (!tagLookup[row.floorplan_name][tag]) {
            tagLookup[row.floorplan_name][tag] = { count: 0, frequency: 0 }
          }
          tagLookup[row.floorplan_name][tag].count += 1
        }
      }
    }
  }

  const floorplanLookup: {
    [floorplanName: string]: {
      amenities: { name: string; count: number; frequency: number }[]
      tags: { name: string; count: number; frequency: number }[]
    }
  } = {}

  Object.keys(amenityLookup).forEach((floorplanName) => {
    floorplanLookup[floorplanName] = { amenities: [], tags: [] }

    Object.keys(amenityLookup[floorplanName]).forEach((amenity) => {
      const { count } = amenityLookup[floorplanName][amenity]
      floorplanLookup[floorplanName].amenities.push({
        name: amenity,
        count,
        frequency: count / totalCount[floorplanName]
      })
    })

    Object.keys(tagLookup[floorplanName] || {}).forEach((tag) => {
      const { count } = tagLookup[floorplanName][tag]
      floorplanLookup[floorplanName].tags.push({
        name: tag,
        count,
        frequency: count / totalCount[floorplanName]
      })
    })
  })

  Object.keys(floorplanLookup).forEach((floorplanName) => {
    floorplanLookup[floorplanName].amenities.sort((a, b) =>
      a.name > b.name ? 1 : b.name > a.name ? -1 : 0
    )
    floorplanLookup[floorplanName].tags.sort((a, b) =>
      a.name > b.name ? 1 : b.name > a.name ? -1 : 0
    )
  })

  return floorplanLookup
}

export function combineWords(words: string[], combiner: string = 'and'): string {
  if (!words || words.length === 0) {
    return ''
  }
  if (words.length === 1) {
    return words[0]
  }
  if (words.length === 2) {
    return words[0] + ' ' + combiner + ' ' + words[1]
  }
  const lastWord = words[words.length - 1]
  const allButLast = words.slice(0, -1)
  return allButLast.join(', ') + ', ' + combiner + ' ' + lastWord
}

export type BasicSchedule =
  | 'Monday'
  | 'Tuesday'
  | 'Wednesday'
  | 'Thursday'
  | 'Friday'
  | 'Saturday'
  | 'Sunday'

const basicScheduleDays: BasicSchedule[] = [
  'Sunday', // Sunday is both 0 and 7
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
  'Sunday'
] as BasicSchedule[]

// Converts a cron expression into a `Schedule[]`.
// A `Schedule` is an array of the days of the week.
export function cronExpressionToBasicSchedule(cronExpression: string): BasicSchedule[] {
  // Example input: 0 12 * * 1,3,4
  const schedule = cronExpression.split(' ') // ['0','12','*','*','1,3,4']

  if (schedule.length === 5 && schedule[4] !== '') {
    const cronDaysOfWeek = schedule[4].split(',').map((_) => _.trim()) //  ['1','3','4']
    if (cronDaysOfWeek[0] === '*') {
      return basicScheduleDays.slice(0, -1) // remove duplicate 'Sunday'
    }
    return cronDaysOfWeek.map((_) => basicScheduleDays[Number.parseInt(_)])
  }

  return []
}

export function basicScheduleToCronExpression(schedule: BasicSchedule[]): string | null {
  if (schedule.length === 0) {
    return null
  }
  const daysOfWeek = schedule.map((_) => basicScheduleDays.indexOf(_))
  return `0 8 * * ${daysOfWeek.join(',')}`
}

// Converts a cron expression into a human-readable schedule,
// e.g. "Weekdays at 9:00pm CST". Falls back to the raw cron on parse failure.
export function humanizeCron(cron: string): string {
  if (!cron) {
    return '-'
  }
  try {
    const days = cronExpressionToBasicSchedule(cron)
    const parts = cron.split(' ')

    const [minutePart, hourPart] = parts
    if (!minutePart || !hourPart) {
      return cron
    }
    const minute = minutePart.padStart(2, '0')
    const hour = Number.parseInt(hourPart)
    const ampm = hour >= 12 ? 'pm' : 'am'
    const hour12 = hour % 12 || 12
    const time = `${hour12}:${minute}${ampm}`

    if (days.length === 7) {
      return `Everyday at ${time} CST`
    }
    // Check for weekends
    if (
      days.length === 2 &&
      days.includes('Saturday' as BasicSchedule) &&
      days.includes('Sunday' as BasicSchedule)
    ) {
      return `Weekends at ${time} CST`
    }
    // Check for weekdays
    if (
      days.length === 5 &&
      !days.includes('Saturday' as BasicSchedule) &&
      !days.includes('Sunday' as BasicSchedule)
    ) {
      return `Weekdays at ${time} CST`
    }

    return `${days.map((d) => d + 's').join(', ')} at ${time} CST`
  } catch {
    return cron
  }
}

export const canExternallyShare = (
  billedPerUnit: boolean,
  billedPerSeat: boolean,
  userFeatureFlags: FeatureFlag[],
  isReportInPortfolio: boolean | null | undefined,
  isUserAdmin: boolean
): boolean => {
  const memberExternalFeatureFlag =
    userFeatureFlags?.includes(FeatureFlag.MEMBER_EXTERNAL_SHARING) ?? false
  const adminExternalFeatureFlag =
    userFeatureFlags?.includes(FeatureFlag.ADMIN_EXTERNAL_SHARING) ?? false

  if (billedPerUnit) {
    if (isReportInPortfolio) {
      return true
    }
    if (isUserAdmin) {
      return adminExternalFeatureFlag || memberExternalFeatureFlag
    }
    return memberExternalFeatureFlag
  }
  if (billedPerSeat) {
    if (isUserAdmin) {
      return adminExternalFeatureFlag || memberExternalFeatureFlag
    }
    return memberExternalFeatureFlag
  }
  return false
}

export const isInternalEmail = (emailToCheck: string, organizationMembers: AccessUser[]) => {
  const email = emailToCheck.toLowerCase()

  return organizationMembers.some((member) => member.email.toLowerCase() === email)
}

export const getFullUrl = (website: string | null) => {
  let urlWithProtocol: string | null = null
  if (website) {
    // Add protocol if missing
    urlWithProtocol =
      website.startsWith('http://') || website.startsWith('https://')
        ? website
        : `https://${website}`
  }
  return urlWithProtocol
}

export const unitsFound = (
  filteredProperties: {
    units: any[]
  }[]
): boolean => {
  return filteredProperties.some(({ units }) => units.length > 0)
}

export const toTitleCase = (str: string) => {
  return str
    .toLowerCase()
    .split(' ')
    .map((word: string) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ')
}

export const humanizeSnakeCase = (str: string): string => {
  return str
    .replace(/_/g, ' ')
    .split(' ')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ')
}

/**
 * Returns whether an error means the property id itself is gone, as opposed to a failure that may
 * succeed on a retry.
 * @param error the error thrown by a property request
 * @returns true only for a 404; any other error, including one carrying no status, returns false
 */
export const isPropertyNotFoundError = (error: unknown): boolean => {
  if (!error || typeof error !== 'object') {
    return false
  }
  const withStatus = error as { status?: unknown; response?: { status?: unknown } }
  return withStatus.status === 404 || withStatus.response?.status === 404
}

const PAYLOAD_TOO_LARGE_MESSAGE =
  'This result is too large. Request fewer properties and try again.'

/**
 * User-facing copy for a 413 from a property list that could not be serialized.
 * Prefers the API body. The generated client puts that body on `error.body`, not `error.message`.
 * @param error the rejection from a property request
 * @returns the message to show, or null when this is not a payload-too-large error
 */
export const payloadTooLargeMessage = (error: unknown): string | null => {
  if (!error || typeof error !== 'object') {
    return null
  }
  const candidate = error as {
    status?: unknown
    body?: { message?: unknown }
    response?: { status?: unknown; data?: { message?: unknown } }
  }
  const status = candidate.status ?? candidate.response?.status
  if (status !== 413) {
    return null
  }
  const fromBody = candidate.body?.message
  if (typeof fromBody === 'string' && fromBody.length > 0) {
    return fromBody
  }
  const fromResponse = candidate.response?.data?.message
  if (typeof fromResponse === 'string' && fromResponse.length > 0) {
    return fromResponse
  }
  return PAYLOAD_TOO_LARGE_MESSAGE
}

// Minimal shape needed to name a property, satisfied by the addresses saved on a report
type NameableAddress = { id: string; building_name: string | null; street_address: string }

/**
 * Names the report properties that failed to load, for listing under a warning banner.
 * @param ids the ids of the properties that failed to load
 * @param subject the report's subject address, used to name the subject when it is the failure
 * @param comparables the report's comparable addresses, used to name a failed comp
 * @returns a row per id still on the report, each with the id and the property's display name
 */
export const propertyLoadFailureRows = (
  ids: string[],
  subject: NameableAddress | null,
  comparables: NameableAddress[]
): { id: string; name: string }[] => {
  return ids.flatMap((id) => {
    const address = subject?.id === id ? subject : comparables.find((_) => _.id === id)
    if (!address) {
      return []
    }
    return {
      id,
      name: address.building_name || address.street_address || id
    }
  })
}

export function makeClosedSquareRing(
  lat: number,
  lon: number,
  meters = 5
): Array<[number, number]> {
  const latRad = (lat * Math.PI) / 180

  const metersPerDegLat =
    111_132.92 -
    559.82 * Math.cos(2 * latRad) +
    1.175 * Math.cos(4 * latRad) -
    0.0023 * Math.cos(6 * latRad)

  const metersPerDegLon =
    111_412.84 * Math.cos(latRad) - 93.5 * Math.cos(3 * latRad) + 0.118 * Math.cos(5 * latRad)

  const dLat = meters / metersPerDegLat
  const dLon = meters / metersPerDegLon

  const nw: [number, number] = [lon - dLon, lat + dLat]
  const ne: [number, number] = [lon + dLon, lat + dLat]
  const se: [number, number] = [lon + dLon, lat - dLat]
  const sw: [number, number] = [lon - dLon, lat - dLat]

  return [nw, ne, se, sw, nw]
}
